import { useCallback, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import { ROUTE_DEFINITIONS } from '@/routes';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import { deviceService } from '@/api/domains/device';
import type { Device, DeviceStatusFilter, DeviceTypeFilter } from '@/api/domains/device';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { formatToDdMmYyyyHHmmss } from '@/utils';


export interface DeviceFiltersState {
  typeFilter: DeviceTypeFilter;
  statusFilter: DeviceStatusFilter;
}

export interface UseDeviceListResult {
  rows: Device[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  filters: DeviceFiltersState;
  setFilters: Dispatch<SetStateAction<DeviceFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<Device>[];
  actions: TableAction<Device>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  debouncedSearchText: string;
}

export function useDeviceList(): UseDeviceListResult {
  const { t: translate } = useTranslation('device');
  const { showApiError, openSidePanel } = useUI();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<DeviceFiltersState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<Device> | null> => {
      const response = await deviceService.list({
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText,
        orderBy: params.sort?.key,
        orderDirection: params.sort?.direction,
        typeFilter: params.filters.typeFilter,
        statusFilter: params.filters.statusFilter,
      }, signal);
      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('errors.loadFailed'));
        return null;
      }
      return {
        items: response.data.data ?? [],
        totalRecords: response.data.pagination.totalRecords,
      };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    debouncedSearchText,
  } = usePaginatedList<Device, DeviceFiltersState>({
    fetchFn,
    initialFilters: { typeFilter: 'ALL', statusFilter: 'ALL' },
    initialSort: { key: 'name', direction: 'ASC' },
    screenCode: ROUTE_DEFINITIONS.DISPOSITIVOS_MARCAJE.code,
  });

  const filterConfig: SimpleFilter[] = useMemo(() => ([
    {
      key: 'typeFilter',
      label: translate('list.filters.typeLabel'),
      multiple: false,
      options: [
        { value: 'ALL', label: translate('list.filters.typeAll') },
        { value: 'RELOJ_CONTROL', label: translate('list.filters.typeRelojControl') },
        { value: 'HUELLERO', label: translate('list.filters.typeHuellero') },
      ],
      defaultValue: 'ALL',
      placeholder: translate('list.filters.typePlaceholder'),
    },
    {
      key: 'statusFilter',
      label: translate('list.filters.statusLabel'),
      multiple: false,
      options: [
        { value: 'ALL', label: translate('list.filters.statusAll') },
        { value: 'CONNECTED', label: translate('list.status.connected') },
        { value: 'DISCONNECTED', label: translate('list.status.disconnected') },
      ],
      defaultValue: 'ALL',
      placeholder: translate('list.filters.statusPlaceholder'),
    },
  ]), [translate]);

  const columns: TableColumn<Device>[] = useMemo(() => [
    {
      key: 'deviceId',
      header: translate('list.columns.deviceId'),
      sortable: true,
      render: (row: Device) => row.deviceId,
    },
    {
      key: 'deviceType',
      header: translate('list.columns.deviceType'),
      sortable: true,
      render: (row: Device) => row.deviceType,
    },
    {
      key: 'name',
      header: translate('list.columns.name'),
      sortable: true,
      render: (row: Device) => row.name,
    },
    {
      key: 'code',
      header: translate('list.columns.code'),
      sortable: true,
      render: (row: Device) => row.code,
    },
    {
      key: 'serialNumber',
      header: translate('list.columns.serialNumber'),
      sortable: true,
      render: (row: Device) => row.serialNumber || '-',
    },
    {
      key: 'locationName',
      header: translate('list.columns.locationName'),
      sortable: true,
      render: (row: Device) => row.locationName || '-',
    },
    {
      key: 'userCount',
      header: translate('list.columns.userCount'),
      sortable: true,
      render: (row: Device) => row.userCount ?? 0,
    },
    {
      key: 'lastConnectionAt',
      header: translate('list.columns.lastConnectionAt'),
      sortable: true,
      render: (row: Device) => formatToDdMmYyyyHHmmss(row.lastConnectionAt) || '-',
    },
    {
      key: 'isConnected',
      header: translate('list.columns.status'),
      sortable: true,
      render: (row: Device) => (
        <Text
          variant="subheader-medium"
          color={row.isConnected ? colors.feedback.success400 : colors.feedback.error400}
          as="span"
        >
          {row.isConnected ? translate('list.status.connected') : translate('list.status.disconnected')}
        </Text>
      ),
    },
  ], [translate]);

  const handleViewDetail = useCallback(
    (row: Device) => {
      openSidePanel({
        component: 'DeviceDetailPanel',
        title: translate('detail.title'),
        props: { device: row },
      });
    },
    [openSidePanel, translate],
  );

  const actions: TableAction<Device>[] = useMemo(() => [
    {
      id: 'view-detail',
      label: translate('common:tableActions.detail'),
      icon: 'eye',
      onClick: handleViewDetail,
    },
  ], [translate, handleViewDetail]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    debouncedSearchText,
  };
}

export default useDeviceList;
